import {ApiManager} from './api/api-manager';
import {VerificationTokenDto} from './api/model/user-response-dto';
import {Status} from './status';
import {EventTracker} from './information-collector/event_tracker';
import {ErrorModel} from "./api/error/error-model";
import {ErrorCode} from "./api/error/error-code";
import {Config} from "../config";
import {SimpleEventEmitter} from "./simple-event-emitter";

export class CaptchaBox {

  readonly statusChangedEvent = new SimpleEventEmitter();
  readonly captchaFinishEvent = new SimpleEventEmitter();

  public status: Status = Status.START
  public config: Config;
  readonly htmlElement: HTMLElement;
  readonly boxCreationTimestamp = new Date();
  readonly eventTracker = new EventTracker();
  public verificationToken: string | null = null;
  formElement: HTMLFormElement;
  honeypotField: HTMLInputElement;
  verificationTokenField: HTMLInputElement;

  public constructor(config: Config, htmlElement: HTMLElement) {
    this.config = config;
    this.htmlElement = htmlElement;
  }

  public setup() {

    this.formElement = this.htmlElement.closest('form');

    if (this.formElement != null) {
      this.honeypotField = this.createHoneypotField();
      this.verificationTokenField = this.createVerificationTokenField();
      this.registerElementFocusListeners();
    } else {
      this.setErrorStatus(new ErrorModel(ErrorCode.NO_FORM_FOUND, "No parent form element found."));
    }
  }

  private createHoneypotField(): HTMLInputElement {

    const honeypotField = document.createElement('input');
    honeypotField.setAttribute('type', 'text');
    honeypotField.setAttribute('name', this.generateHoneypotName());
    honeypotField.setAttribute('style', 'display:none');
    honeypotField.setAttribute('tabindex', '-1');

    this.formElement.appendChild(honeypotField);

    return honeypotField;
  }

  private generateHoneypotName(): string {
    const baseNames = [
      "userDetail", "confirmAction", "userData", "verifyContact", "sessionInfo",
      "profileUpdate", "accountVerify", "securityCheck", "membershipInfo", "customerPreference",
      "siteFeedback", "userExperience", "navigationData", "serviceUsage", "interactionTrack",
      "clientDetail", "transactionVerify", "operationStatus", "activityLog", "registrationData",
      "processCheck", "confirmationStatus", "userContribution", "accessValidation", "engagementLevel",
      "validationCode", "preferenceSetting", "userEngage", "activityConfirm", "serviceInteraction"
    ];
    const randomIndex = Math.floor(Math.random() * baseNames.length);
    const randomNumber = Math.floor(Math.random() * 1000);

    return baseNames[randomIndex] + randomNumber;
  }

  private createVerificationTokenField(): HTMLInputElement {

    const verificationTokenField = document.createElement('input');
    verificationTokenField.setAttribute('type', 'text');
    verificationTokenField.setAttribute('name', this.config.tokenFieldName);
    verificationTokenField.setAttribute('style', 'display:none');
    verificationTokenField.setAttribute('tabindex', '-1');

    return verificationTokenField;
  }

  private registerElementFocusListeners() {

    this.formElement.addEventListener('keydown', (event) => {
      if (event instanceof KeyboardEvent) {
        this.autostartVerification();
      }
    });

    this.formElement.querySelectorAll('input:not([data-autostart="false"]), select:not([data-autostart="false"]), textarea:not([data-autostart="false"])').forEach(element => {
      element.addEventListener('focusin', () => {
        this.autostartVerification();
      })
    });
  }

  public autostartVerification() {
    if (this.config.autostart) {
      this.startVerification();
    }
  }

  private apiManager: ApiManager | null = null;

  public startVerification() {
    if (this.status !== Status.START) {
      return;
    }

    this.switchStatus(Status.RUNNING);

    this.apiManager = new ApiManager(
      this,
      (verificationToken) => {
        this.eventTracker.stop();
        this.finishVerification(verificationToken);
        this.apiManager = null;
      },
      (reason) => {
        this.eventTracker.stop();
        this.setErrorStatus(reason);
        this.apiManager = null;
      }
    );

    this.apiManager.verify();
  }

  private finishVerification(verificationToken: VerificationTokenDto): void {

    const jsonString = JSON.stringify(verificationToken);
    const base64String = btoa(jsonString);
    this.verificationToken = base64String;

    this.verificationTokenField.setAttribute("value", base64String);
    this.formElement.appendChild(this.verificationTokenField);

    this.handleCaptchaSolved();
  }

  public handleCaptchaSolved(): void {

    if (this.verificationToken) {
      this.captchaFinishEvent.emit("captchaSolved", this.verificationToken);
      this.switchStatus(Status.DONE);
    }
  }

  private switchStatus(nextStatus: Status): void {

    this.status = nextStatus;
    this.statusChangedEvent.emit('statusChanged', nextStatus);
  }

  public setErrorStatus(errorModel: ErrorModel): void {
    this.switchStatus(Status.FAILED);
    this.captchaFinishEvent.emit("captchaFailed", errorModel);
  }

  public reset(): void {

    if (this.apiManager) {
      this.apiManager.reset();
      this.apiManager = null;
    }

    this.honeypotField.removeAttribute("value");

    this.verificationToken = null;
    this.verificationTokenField.removeAttribute("value");
    if (this.verificationTokenField && this.formElement.contains(this.verificationTokenField)) {
      this.formElement.removeChild(this.verificationTokenField);
    }

    this.eventTracker.reset();

    this.switchStatus(Status.START);
    this.captchaFinishEvent.emit("captchaReset");
  }

  public setNewConfig(config: Config): void {
    this.config = config;
  }
}
